import React, { useState } from 'react';

import { Box } from '@clutter/clean';

import { RecaptchaWidget } from '@shared/components/recaptcha/widget';
import { RECAPTCHA_ENABLED } from '@shared/config/recaptcha';
import { EventSchema, Portal__LoginAttempt } from '@shared/wt';

import { Button } from '@portal/components/shared/button';
import { Banner as ErrorBanner } from '@portal/components/shared/error';
import { Input, InputLabel } from '@portal/components/shared/form';
import { activateURL } from '@portal/config/routes';
import { useTrack } from '@portal/initializers/wt';
import { client } from '@portal/libraries/apollo';
import { Status, useSignInMutation } from '@portal/schema';

import { TextLink } from '../shared/text';
import { LoginMFAForm } from './mfa_form';

const DEFAULT_ERROR = "We couldn't find an account with that email and password.";

export const LoginForm: React.FC = () => {
  const [email, setEmail] = useState<string>(() => new URLSearchParams(window.location.search).get('email') || '');
  const [password, setPassword] = useState<string>('');
  const [error, setError] = useState<string | undefined>(undefined);
  const [execute, { loading, data, reset }] = useSignInMutation({ client });
  const track = useTrack();
  const mfaRequestID = data?.result?.mfaRequestID;

  const login = async (recaptcha: string | null) => {
    const response = await execute({
      variables: {
        input: {
          email,
          password,
          recaptcha,
        },
      },
    });

    const status = response.data?.result?.status;
    const mfa = !!response.data?.result?.mfaRequestID;
    if (status === Status.Ok) {
      if (!mfa) location.replace('/restore');
    } else {
      if (typeof grecaptcha !== 'undefined') grecaptcha.enterprise.reset();
      setError(response.data?.result?.error ?? DEFAULT_ERROR);
    }
  };

  const onSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    event.stopPropagation();
    if (loading) return;

    setError(undefined);

    const params: Portal__LoginAttempt = {
      schema: EventSchema.Portal__LoginAttempt,
      action: 'click',
      metadata: { email },
    };
    track(params);

    if (!email || !password) {
      setError('Please enter your login credentials to continue.');
      return;
    }

    const recaptcha = RECAPTCHA_ENABLED ? grecaptcha.enterprise.getResponse() : null;

    if (!recaptcha && RECAPTCHA_ENABLED) {
      setError('Please complete the reCAPTCHA to continue.');
      return;
    }

    await login(recaptcha);
  };

  if (mfaRequestID)
    return (
      <LoginMFAForm
        email={email}
        password={password}
        mfaRequestID={mfaRequestID}
        onReset={() => {
          reset();
        }}
      />
    );

  return (
    <Box.Flex flexDirection="column" gap="24px">
      <form onSubmit={onSubmit}>
        <Box.Flex flexDirection="column" gap="16px">
          {error && <ErrorBanner>{error}</ErrorBanner>}

          <Box.Flex flexDirection="column">
            <InputLabel htmlFor="customer-email">Email</InputLabel>
            <Input
              id="customer-email"
              type="email"
              value={email}
              onChange={(event) => setEmail(event.target.value || '')}
              autoFocus
            />
          </Box.Flex>

          <Box.Flex flexDirection="column">
            <InputLabel htmlFor="customer-password">Password</InputLabel>
            <Input
              id="customer-password"
              type="password"
              value={password}
              onChange={(event) => setPassword(event.target.value || '')}
              autoComplete="off"
            />
          </Box.Flex>

          {RECAPTCHA_ENABLED && <RecaptchaWidget />}

          <Button kind="primary" size="medium" loading={loading}>
            Log in
          </Button>
        </Box.Flex>
      </form>

      <Box.Flex justifyContent="space-between">
        <TextLink href="/password/new">Forgot password?</TextLink>
        <TextLink href="https://www.clutter.com">Get started</TextLink>
      </Box.Flex>

      <Box.Flex alignItems="center" flexDirection="column">
        <p>Don’t have a password yet?</p>
        <TextLink href={activateURL()}>Activate your Clutter account</TextLink>
      </Box.Flex>
    </Box.Flex>
  );
};
